import React, { useMemo } from 'react';
import { Box, List, ListItem, ListItemIcon, ListItemText, ListItemButton, Tooltip, Chip } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import PeopleIcon from '@mui/icons-material/People';
import BalanceIcon from '@mui/icons-material/Balance';
import PersonIcon from '@mui/icons-material/Person';
import FlagIcon from '@mui/icons-material/Flag';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SearchIcon from '@mui/icons-material/Search';
import DashboardIcon from '@mui/icons-material/Dashboard';
import FolderIcon from '@mui/icons-material/Folder';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SchoolIcon from '@mui/icons-material/School';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventNoteIcon from '@mui/icons-material/EventNote';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import BadgeIcon from '@mui/icons-material/Badge';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';

import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '@mui/material/styles';

const menuItems = [
    {
        section: '1',
        items: [
            {
                text: 'Activity Dashboard',
                icon: <PersonIcon />,
                path: '/',
                module: 'dashboard',
                action: 'view',
                description: 'This section provides an overview of all important information for your role and shortcut links to access the individual sections.'
            },
            {
                text: 'Sales',
                icon: <RequestQuoteIcon />,
                path: '/sales',
                modules: ['enquiries', 'quotations', 'purchase_orders'],
                action: 'view',
                description: 'Enquiries, quotations and purchase orders — from the customer request to the service order.'
            },
            {
                text: 'Service Orders',
                icon: <FlagIcon />,
                path: '/service-orders',
                module: 'service_orders',
                action: 'view',
                description: 'Create and manage service orders, track their progress, and handle customer requests.'
            },

        ]
    },
    {
        section: '2',
        items: [
            {
                text: 'Inventory Management',
                icon: <SearchIcon />,
                path: '/inventory',
                module: 'inventory',
                action: 'view',
                description: 'Track equipment and consumables, manage stock levels, and handle inventory movements.'
            },
            {
                text: 'Users',
                icon: <PeopleIcon />,
                path: '/users',
                module: 'users',
                action: 'view',
                description: 'Manage user accounts, roles, permissions, and user-related information.'
            },
            {
                text: 'Document Management',
                icon: <FolderIcon />,
                path: '/documents',
                module: 'documents',
                action: 'view',
                description: 'Search, filter, preview, and manage all documents. Download documents by service order, customer, date range, and more.'
            },
            {
                text: 'Reporting',
                icon: <BalanceIcon />,
                path: '/reporting',
                module: 'reporting',
                action: 'view',
                description: 'Generate detailed reports, analytics, and insights from system data.'
            },
            {
                text: 'Invoice & Billing',
                icon: <AccountBalanceIcon />,
                path: '/ar-dashboard',
                module: 'ar_dashboard',
                action: 'view',
                description: 'Accounts receivable summary, aging buckets, and outstanding invoice tracking.'
            },
            {
                text: 'Training',
                icon: <SchoolIcon />,
                path: '/training',
                module: 'training',
                action: 'view',
                description: 'Manage courses, sessions, tests, certifications, and trainee portal.'
            },
            {
                text: 'Workforce Management',
                icon: <BadgeIcon />,
                path: '/workforce-management',
                aliasPaths: ['/attendance', '/leave', '/expenses'],
                modules: ['attendance', 'leave', 'expenses'],
                action: 'view',
                description: 'Manage employee attendance, leave requests, and expense claims.'
            },
            {
                text: 'Master Data Management',
                icon: <PersonIcon />,
                path: '/master-data',
                module: 'master_data',
                action: 'view',
                description: 'Manage reference data, service types, locations, and system configurations.'
            },

            {
                text: 'Settings',
                icon: <TrendingUpIcon />,
                path: '/settings',
                alwaysVisible: true,
                description: 'Change password, configure system settings, manage roles and permissions.'
            }
        ]
    },
    {
        section: '3',
        items: [
            {
                text: 'Reflection Dashboard',
                icon: <DashboardIcon />,
                path: '/reflection-dashboard',
                module: 'admin_dashboard',
                action: 'view',
                description: 'Comprehensive overview of system performance, staff schedules, and operational metrics for administrators.'
            }
        ]
    }
];

const Sidebar = ({ open = true }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const theme = useTheme();
    // AuthContext already calls refreshUserData() as a background task after token
    // verification. A second call here would duplicate every /api/users/me/ request.

    const permissions = user?.role?.permissions;

    const isCustomer = user?.role?.name === 'Customer';
    const isTrainee = user?.role?.name === 'Trainee';

    const filteredMenuItems = useMemo(() => {
        if (!permissions) return [];

        return menuItems.map(section => ({
            ...section,
            items: section.items
                .filter(item => {
                    // Items marked alwaysVisible are shown for all authenticated users
                    if (item.alwaysVisible) return true;
                    
                    // Admin and Superadmin can see everything
                    if (user?.role?.name === 'Superadmin' || user?.role?.name === 'Admin') return true;
                    
                    if (item.modules) {
                        const hasAnyModule = item.modules.some(m => {
                            const mp = permissions[m];
                            if (!mp) return false;
                            const hasView = mp[item.action || 'view'] === true;
                            const hasAny = ['view', 'add', 'change', 'delete'].some(a => mp[a] === true);
                            return hasView && hasAny;
                        });
                        return hasAnyModule;
                    }

                    const modulePerms = permissions[item.module];
                    if (!modulePerms) return false;
                    const hasViewPermission = modulePerms[item.action] === true;
                    // Calculate visibility from any permission being enabled
                    const hasAnyPermission = ['view', 'add', 'change', 'delete'].some(a => modulePerms[a] === true);
                    return hasViewPermission && hasAnyPermission;
                })
                .map(item => {
                    // Customers go straight to the flat report list, not the SO selector
                    if (item.module === 'reporting' && isCustomer) {
                        return { ...item, path: '/customer/reports' };
                    }
                    if (isTrainee && (item.module === 'dashboard' || item.module === 'training')) {
                        return { ...item, path: '/training/portal' };
                    }
                    return item;
                })
        })).filter(section => section.items.length > 0);
    }, [permissions, isCustomer, isTrainee]);

    return (
        <Box
            sx={{
                width: open ? theme.sidebar.width : 0,
                minWidth: open ? theme.sidebar.width : 0,
                maxWidth: open ? theme.sidebar.width : 0,
                bgcolor: 'sidebar.bg',
                color: 'sidebar.selectedText',
                display: 'flex',
                flexDirection: 'column',
                py: 2,
                px: 0,
                height: '100vh',
                position: 'fixed',
                left: 0,
                top: 0,
                zIndex: theme.zIndex.drawer,
                overflowY: 'auto',
                overflowX: 'hidden',
                borderRight: open ? '1px solid' : 'none',
                borderColor: 'sidebar.divider',
                opacity: open ? 1 : 0,
                visibility: open ? 'visible' : 'hidden',
                transition: theme.transitions.create(['width', 'opacity', 'visibility'], {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.enteringScreen,
                }),
                '&::-webkit-scrollbar': {
                    width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                    background: 'sidebar.divider',
                },
                '&::-webkit-scrollbar-thumb': {
                    background: 'grey.600',
                    borderRadius: 1,
                },
                '&::-webkit-scrollbar-thumb:hover': {
                    background: 'grey.500',
                },
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', pl: 2, mb: 2, mt: 1 }}>
                <Box
                    component="img"
                    src="/Reflection_logo_with_text_dark_background.svg"
                    alt="Reflection Logo"
                    sx={{
                        width: 160,
                        height: 'auto',
                        ml: 1,
                    }}
                />
            </Box>

            {filteredMenuItems.map((section, sectionIndex) => (
                <Box key={section.section}>
                    {/* Section Header */}
                    {/* <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        pl: 3,
                        mb: 2,
                        color: '#ffffff',
                        fontSize: '12px',
                        fontWeight: 600,
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase'
                    }}>
                        {section.section === 'TEAM PORTFOLIO' && (
                            <PeopleIcon sx={{ mr: 1.5, fontSize: 16, color: '#ffffff' }} />
                        )}
                        {section.section === 'REBALANCE PORTFOLIO' && (
                            <BalanceIcon sx={{ mr: 1.5, fontSize: 16, color: '#ffffff' }} />
                        )}
                        {section.section}
                    </Box> */}

                    {/* Section Items */}
                    <List sx={{ px: 0, py: 0, width: '100%' }}>
                        {section.items.map((item) => {
                            // Use exact match for root '/' to avoid it matching all routes;
                            // for all other paths use startsWith so sub-routes keep the highlight.
                            const selected = item.path === '/'
                                ? location.pathname === '/'
                                : location.pathname.startsWith(item.path) || (item.aliasPaths && item.aliasPaths.some(p => location.pathname.startsWith(p)));
                            return (
                                <Tooltip
                                    key={item.path}
                                    title={open ? item.description : `${item.text} — ${item.description}`}
                                    placement="right"
                                    arrow
                                >
                                    <ListItem
                                        disablePadding
                                        sx={{
                                            mb: 0.5,
                                            position: 'relative',
                                            minHeight: 44,
                                            width: '100%',
                                            maxWidth: '100%',
                                        }}
                                    >
                                        {selected && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    left: 0,
                                                    top: 0,
                                                    bottom: 0,
                                                    width: 4,
                                                    bgcolor: 'sidebar.highlight',
                                                    zIndex: 2,
                                                }}
                                            />
                                        )}
                                        <ListItemButton
                                            onClick={() => navigate(item.path)} // eslint-disable-line react/jsx-no-bind
                                            selected={selected}
                                            sx={{
                                                zIndex: 1,
                                                pl: 3,
                                                minHeight: 44,
                                                background: selected ? 'grey.800' : 'transparent',
                                                color: selected ? 'sidebar.selectedText' : 'sidebar.text',
                                                fontWeight: selected ? 600 : 400,
                                                '&:hover': {
                                                    background: selected ? 'grey.800' : 'sidebar.divider',
                                                },
                                                width: '100%',
                                                maxWidth: '100%',
                                                borderRadius: 0,
                                            }}
                                        >
                                            <ListItemIcon
                                                sx={{
                                                    color: selected ? 'sidebar.selectedText' : 'sidebar.text',
                                                    minWidth: 30,
                                                    '& .MuiSvgIcon-root': {
                                                        fontSize: '22px',
                                                    },
                                                }}
                                            >
                                                {item.icon}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <span>{item.text}</span>
                                                        {item.beta && (
                                                            <Chip
                                                                label="Beta"
                                                                size="small"
                                                                sx={{
                                                                    height: 16,
                                                                    fontSize: '9px',
                                                                    fontWeight: 700,
                                                                    letterSpacing: '0.5px',
                                                                    textTransform: 'uppercase',
                                                                    bgcolor: 'rgba(245, 158, 11, 0.15)',
                                                                    color: '#f59e0b',
                                                                    border: '1px solid rgba(245, 158, 11, 0.4)',
                                                                    borderRadius: '4px',
                                                                    '& .MuiChip-label': {
                                                                        px: '5px',
                                                                        py: 0,
                                                                    },
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                }
                                                primaryTypographyProps={{
                                                    fontWeight: selected ? 'bold' : 400,
                                                    color: selected ? 'sidebar.selectedText' : 'sidebar.text',
                                                    fontSize: '14px',
                                                    component: 'div',
                                                }}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                </Tooltip>
                            );
                        })}
                    </List>

                    {/* Divider between sections */}
                    {/* {sectionIndex < filteredMenuItems.length - 1 && (
                        <Divider sx={{
                            mx: 2,
                            my: 2,
                            borderColor: 'sidebar.divider',
                            opacity: 0.5
                        }} />
                    )} */}
                </Box>
            ))}
        </Box>
    );
};

export default Sidebar; 